import React, { useMemo } from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { OccupancyDailyResponse } from '../../../../services/occupancy/response/OccupancyDailyResponse';

// Definisikan tipe untuk satu item data agar lebih aman
type DailyDataItem = OccupancyDailyResponse['data'][0];

interface OccupancyTableProps {
    // [3. GANTI PROPS] Gunakan tipe respons yang baru
    data: OccupancyDailyResponse;
}

const OccupancyTable: React.FC<OccupancyTableProps> = ({ data }) => {
    const columnsReservations = [
        { label: "Date", key: "date" },
        { label: "Rooms", key: "total_rooms" },
        { label: "Room Sold", key: "total_booked" },
        { label: "Occupancy", key: "occupancy" },
        { label: "Available", key: "available" },
    ];

    // [4. HITUNG TOTAL SECARA MANUAL] Gunakan useMemo untuk efisiensi
    const totals = useMemo(() => {
        if (!data?.data || data.data.length === 0) {
            return { total_rooms: 0, total_booked: 0, available: 0, occupancy: "0.00" };
        }

        const sum = data.data.reduce(
            (acc, row) => {
                acc.total_rooms += row.total_rooms;
                acc.total_booked += row.total_booked;
                acc.available += row.available;
                return acc;
            },
            { total_rooms: 0, total_booked: 0, available: 0 }
        );

        const avgOccupancy = sum.total_rooms > 0
            ? ((sum.total_booked / sum.total_rooms) * 100).toFixed(2)
            : "0.00";

        return { ...sum, occupancy: avgOccupancy };
    }, [data]); // Kalkulasi ulang hanya jika data berubah

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Occupancy Report - Daily", 14, 10);

        const tableData = data.data.map(row =>
            columnsReservations.map(col => {
                const value = row[col.key as keyof DailyDataItem];
                return col.key === 'occupancy' ? `${value}%` : value;
            })
        );

        autoTable(doc, {
            head: [columnsReservations.map(col => col.label)],
            body: tableData,
            // [5. GUNAKAN HASIL PERHITUNGAN] Ambil total dari objek 'totals'
            foot: [
                columnsReservations.map(col =>
                    col.key === "date" ? "Total" :
                        col.key === "occupancy" ? `${totals[col.key as keyof typeof totals]}%` :
                            totals[col.key as keyof typeof totals]
                ),
            ],
        });

        doc.save("OccupancyReport_Daily.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columnsReservations.map(col => col.label),
            ...data.data.map(row =>
                columnsReservations.map(col => {
                    const value = row[col.key as keyof DailyDataItem];
                    return col.key === 'occupancy' ? `${value}%` : value;
                })
            ),
            // [5. GUNAKAN HASIL PERHITUNGAN] Ambil total dari objek 'totals'
            columnsReservations.map(col =>
                col.key === "date" ? "Total" :
                    col.key === "occupancy" ? `${totals[col.key as keyof typeof totals]}%` :
                        totals[col.key as keyof typeof totals]
            ),
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "OccupancyReport_Daily.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsReservations.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data && data.data.length > 0 ? (
                            data.data.map((row: DailyDataItem, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columnsReservations.map((col, colIndex) => (
                                        <td key={colIndex}>
                                            {col.key === 'occupancy'
                                                ? `${row[col.key as keyof DailyDataItem]}%`
                                                : row[col.key as keyof DailyDataItem]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columnsReservations.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            {/* [5. GUNAKAN HASIL PERHITUNGAN] Ambil total dari objek 'totals' */}
                            {columnsReservations.map((col, index) => (
                                <td key={index}>
                                    <strong>
                                        {col.key === "date" ? "Total" :
                                            col.key === "occupancy" ? `${totals[col.key as keyof typeof totals]}%` :
                                                totals[col.key as keyof typeof totals]}
                                    </strong>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default OccupancyTable;